import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Autocomplete, Avatar, Button, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ArrowDownward, ArrowUpward, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { discoverySample, playerPosition } from '../../utils/playerDiscovery';
import { playerComparisonMetric, playerKey, playerName, playerPer90 } from '../../utils/playerRanking';
import { comparisonAdvancedCategories, comparisonHighlights } from '../../utils/playerComparison';
import ComparisonRadar from '../charts/ComparisonRadar';
import './playerComparison.css';

const COUNTS = [
  ['golesTotales', 'comparisonGoals', 1], ['golesPenaltis', 'comparisonPenalties', 0],
  ['amarillasTotales', 'yellow', -1], ['segundasAmarillasTotales', 'secondYellow', -1], ['rojasTotales', 'red', -1],
];
const teamKey = player => String(player.idEquipo ?? player.codEquipo ?? player.nombreEquipo ?? player.equipo ?? '__unregistered__');
const sameContext = (first, second) => ['idTemporada', 'idCompeticion', 'idGrupo']
  .every(key => String(first[key] ?? '') === String(second[key] ?? ''));
const samePlayer = (first, second) => first && second && sameContext(first, second) && (playerKey(first) === playerKey(second)
  || (first.idJugador != null && second.idJugador != null && String(first.idJugador) === String(second.idJugador)));

export default function PlayerComparison({ players, playersBySlot, ids, onChange, minimum, profileUrl, profileState,
  contexts = [], contextControls = [], notices = [], selectionDisabled = [] }) {
  const { t, i18n } = useTranslation('directories');
  const { t: advancedT } = useTranslation('advanced');
  const tabsId = useId();
  const [mode, setMode] = useState('rates');
  const [category, setCategory] = useState('overview');
  const [teamChoices, setTeamChoices] = useState([null, null]);
  const [expandedContexts, setExpandedContexts] = useState([null, null]);
  const pools = playersBySlot || [players, players];
  const selected = ids.map((id, slot) => pools[slot].find(player => playerKey(player) === id));
  const format = (value, percent = false) => value === null ? '—' :
    `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value === 0 ? 0 : value)}${percent ? '%' : ''}`;
  const changePlayer = (slot, player) => {
    const next = [...ids];
    next[slot] = player ? playerKey(player) : '';
    onChange(next);
  };
  const both = selected.every(Boolean);
  const distinct = both && !samePlayer(...selected);
  const sameSource = both && selected[0].estadisticasAvanzadas?.fuente
    && selected[0].estadisticasAvanzadas.fuente === selected[1].estadisticasAvanzadas?.fuente;
  const categoryLabel = id => id === 'overview' ? t('comparison.categories.overview')
    : advancedT(['key', 'all'].includes(id) ? id : `groups.${id}`, { defaultValue: id });
  const advanced = comparisonAdvancedCategories(selected, { mode, minimum }).map(item => ({ ...item,
    rows: item.rows.map(row => {
      const base = row.id === 'FS_RATING' ? t('discovery.metrics.rating')
        : advancedT(`metrics.${row.id}`, { defaultValue: row.id.startsWith('FUENTE_')
          ? advancedT('sourceMetric', { id: row.id.slice(7) }) : row.id.replaceAll('_', ' ') });
      return { ...row, radarLabel: t(`comparison.radarLabels.${row.id}`, { defaultValue: base }),
        label: row.percent ? `${base} (%)` : row.operation === 'suma' && mode !== 'totals'
          ? t('per90', { metric: base }) : base };
    }),
  }));
  const rating = advanced.find(item => item.id === 'key')?.rows.find(row => row.id === 'FS_RATING');
  const tabs = [{ id: 'overview' }, ...advanced];
  const active = tabs.find(item => item.id === category) || tabs[0];
  const summaryRows = [
    { id: 'minutesPerMatch', label: t('discovery.metrics.minutesPerMatch'), direction: 0,
      values: selected.map(player => player ? discoverySample(player, 'minutesPerMatch').value : null) },
    { id: 'startsPercent', label: t('discovery.metrics.startsPercent'), direction: 0, percent: true,
      values: selected.map(player => player ? discoverySample(player, 'startsPercent').value : null) },
    ...COUNTS.map(([id, label, direction]) => ({
      id, label: mode === 'totals' ? t(label) : t('per90', { metric: t(label) }), direction,
      values: selected.map(player => player ? mode === 'totals' ? playerComparisonMetric(player, id) : playerPer90(player, id) : null),
    })),
    ...(rating ? [rating] : []),
  ];
  const highlighted = active.id === 'overview' ? [] : comparisonHighlights(active);
  const rows = active.id === 'overview' ? summaryRows : [
    ...highlighted, ...active.rows.filter(row => !highlighted.some(metric => metric.id === row.id)),
  ];
  const difference = values => values.every(value => value !== null) ? Number((values[0] - values[1]).toFixed(2)) : null;
  const signed = (value, percent) => value === null ? '—' : percent
    ? t('comparison.percentagePoints', { value: `${value > 0 ? '+' : ''}${format(value)}` })
    : `${value > 0 ? '+' : ''}${format(value)}`;
  const metricCards = distinct ? rows.map(({ id, label, values, direction, percent, samples, derived }) => {
    const delta = difference(values);
    const result = delta === null || delta === 0 ? 'equal' : direction === 0 ? 'neutral'
      : delta * direction > 0 ? 'ahead' : 'behind';
    const max = Math.max(0, ...values.filter(value => value !== null).map(Math.abs));
    const negative = values.some(value => value !== null && value < 0);
    const Arrow = delta > 0 ? ArrowUpward : ArrowDownward;
    return <div key={id} className="pc-metric" role="group" aria-label={label}>
      <div className="pc-metric-heading"><h4>{label}</h4>
        <span className={`pc-difference pc-${result}`} aria-label={t('comparison.difference', { name: playerName(selected[0]), metric: label })}>
          {delta !== null && delta !== 0 && <Arrow aria-hidden="true" />}
          {signed(delta, percent)}
        </span>
      </div>
      {values.map((value, slot) => <div key={slot} className={`pc-bar-row pc-player-${slot}`}>
        <span className="pc-sr-only">{playerName(selected[slot])}</span>
        <div className={`pc-bar-track${negative ? ' pc-signed' : ''}`} aria-hidden="true"><span style={{
          width: `${value !== null && max > 0 ? Math.abs(value) / max * (negative ? 50 : 100) : 0}%`,
          ...(negative ? { left: `${value < 0 ? 50 - Math.abs(value) / max * 50 : 50}%` } : {}),
        }} /></div>
        <strong>{format(value, percent)}</strong>
      </div>)}
      {samples && <p className="pc-coverage">{samples.map((sample, slot) =>
        <span key={slot} className={`pc-player-${slot}`}><span className="pc-sr-only">{playerName(selected[slot])}: </span>
          {t('discovery.sample', { matches: sample.matches ?? '—', minutes: sample.minutes ?? '—' })}
          {percent && sample.attempts !== null && sample.successes !== null
            && ` · ${t('comparison.attempts', { successes: format(sample.successes), attempts: format(sample.attempts) })}`}
        </span>)}</p>}
      {derived && <p className="pc-coverage">{advancedT(`formulas.${id}`)}</p>}
    </div>;
  }) : [];
  return <section className="pc-comparison" aria-label={t('comparePlayers')}>
    <header className="pc-heading"><h2>{t('comparison.selection')}</h2>
      <Button disabled={!ids.some(Boolean)} onClick={() => { setTeamChoices([null, null]); onChange(['', '']); }}>
        {t('clearComparison')}
      </Button>
    </header>
    <div className="pc-dossiers">{[0, 1].map(slot => {
      const player = selected[slot];
      const choice = teamChoices[slot];
      const contextKey = JSON.stringify(['temporada', 'competicion', 'grupo'].map(key => contexts[slot]?.[key] ?? ''));
      const selectedTeam = choice?.playerId === ids[slot] && choice?.contextKey === contextKey
        ? choice.team : player ? teamKey(player) : '';
      const teams = [...new Map(pools[slot].map(candidate => [teamKey(candidate), {
        id: teamKey(candidate), name: candidate.nombreEquipo || candidate.equipo || t('comparison.teamMissing'),
      }])).values()].sort((first, second) => first.name.localeCompare(second.name, i18n.language));
      const candidates = pools[slot].filter(candidate => teamKey(candidate) === selectedTeam
        && !samePlayer(candidate, selected[1 - slot])).sort((first, second) =>
        playerName(first).localeCompare(playerName(second), i18n.language));
      const team = teams.find(option => option.id === selectedTeam) || null;
      return <article key={slot} className={`dr-panel pc-dossier pc-player-${slot}`} aria-label={t('comparisonPlayer', { number: slot + 1 })}>
        {contextControls[slot] && <>
          <details className="pc-slot-context" open={expandedContexts[slot] ?? !player} onToggle={event => {
            const open = event.currentTarget.open;
            setExpandedContexts(previous => previous[slot] === open ? previous : previous.map((value, index) => index === slot ? open : value));
          }}>
            <summary>{t('comparison.playerContext', { number: slot + 1 })}</summary>
            {contextControls[slot]}
          </details>
          <p className="pc-context-label">{['temporada', 'federacion', 'categoria', 'competicion', 'grupo']
            .map(key => contexts[slot]?.[`${key}Nombre`] || contexts[slot]?.[key]).filter(Boolean).join(' · ')
            || t('comparison.chooseContext')}</p>
        </>}
        {notices[slot]}
        <div className="pc-picker">
          <Autocomplete options={teams} value={team} getOptionLabel={option => option.name} getOptionKey={option => option.id}
            disabled={Boolean(selectionDisabled[slot])}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={t('comparison.noTeams')}
            onChange={(_, value) => {
              setTeamChoices(previous => previous.map((entry, index) => index === slot
                ? { playerId: '', team: value?.id || '', contextKey } : entry));
              changePlayer(slot, null);
            }}
            renderInput={input => <TextField {...input} label={t('comparison.team', { number: slot + 1 })} />} />
          <Autocomplete options={candidates} value={player || null} disabled={!selectedTeam || Boolean(selectionDisabled[slot])}
            getOptionLabel={option => playerName(option)}
            isOptionEqualToValue={(option, value) => playerKey(option) === playerKey(value)}
            noOptionsText={t('comparison.noPlayers')}
            onChange={(_, value) => {
              if (!value) setTeamChoices(previous => previous.map((entry, index) => index === slot
                ? { playerId: '', team: selectedTeam, contextKey } : entry));
              changePlayer(slot, value);
            }}
            renderOption={(props, option) => <li {...props} key={playerKey(option)}>
              <span>{playerName(option)}<small className="pc-option-meta">{playerPosition(option) || t('discovery.positionUnknown')}</small></span>
            </li>}
            renderInput={input => <TextField {...input} label={t('comparisonPlayer', { number: slot + 1 })}
              placeholder={t(selectedTeam ? 'comparison.searchPlayer' : 'comparison.chooseTeam')} />} />
        </div>
        {player ? <>
          <header className="pc-identity"><Avatar src={player.foto || undefined} alt="">{playerName(player)[0]}</Avatar>
            <div><h3>{playerName(player)}</h3><p>{player.nombreEquipo || player.equipo}</p>
              <small>{playerPosition(player) || t('discovery.positionUnknown')}</small></div>
          </header>
          <dl className="pc-sample">{[['partidosJugados', 'matches'], ['minutosJugados', 'comparisonMinutes']].map(([key, label]) =>
            <div key={key}><dt>{t(label)}</dt><dd>{format(playerComparisonMetric(player, key))}</dd></div>)}</dl>
          {profileUrl && <Button component={Link} to={profileUrl(player)} state={profileState?.(player)}
            endIcon={<ArrowForward />}>{t('discovery.profileShort')}</Button>}
        </> : <div className="pc-placeholder"><h3>{t('comparisonPlayer', { number: slot + 1 })}</h3>
          <p>{t(selectedTeam ? 'comparison.pickPlayer' : 'comparison.pickTeam')}</p></div>}
      </article>;
    })}</div>
    {distinct && !sameContext(...selected) && <Alert className="pc-context-warning" severity="info">
      {t('comparison.differentContexts')}
    </Alert>}
    {distinct ? <section className="dr-panel pc-chart" aria-label={t('comparison.visual')}>
      <header className="pc-heading"><h3>{t('comparison.visual')}</h3>
        <ToggleButtonGroup exclusive value={mode}
          onChange={(_, value) => value && setMode(value)} aria-label={t('comparison.metricMode')}>
          <ToggleButton value="rates">{t('comparison.rates')}</ToggleButton>
          <ToggleButton value="totals">{t('comparison.totals')}</ToggleButton>
        </ToggleButtonGroup>
      </header>
      <Tabs value={active.id} onChange={(_, value) => setCategory(value)} variant="scrollable" scrollButtons="auto"
        allowScrollButtonsMobile aria-label={t('comparison.categoriesLabel')} className="pc-tabs">
        {tabs.map(item => <Tab key={item.id} value={item.id} label={categoryLabel(item.id)}
          id={`${tabsId}-tab-${item.id}`} aria-controls={`${tabsId}-panel-${item.id}`} />)}
      </Tabs>
      {tabs.map(tab => <div key={tab.id} role="tabpanel" id={`${tabsId}-panel-${tab.id}`}
        aria-labelledby={`${tabsId}-tab-${tab.id}`} hidden={tab.id !== active.id} tabIndex={0} className="pc-tabpanel">
      {tab.id === active.id && <>
      <h4 className="pc-category-title">{categoryLabel(active.id)}</h4>
      <div className="pc-legend"><span className="pc-player-0">{playerName(selected[0])}</span>
        <span className="pc-player-1">{playerName(selected[1])}</span></div>
      <div className={active.id === 'overview' ? '' : 'pc-category-comparison'}>
      {active.id !== 'overview' && <ComparisonRadar className="pc-radar"
        axes={highlighted.map(row => ({ key: row.id, label: row.radarLabel, values: row.values }))}
        title={t('comparison.radarTitle', { category: categoryLabel(active.id) })}
        description={t('comparison.radarScale')} emptyText={t('comparison.radarUnavailable')}
        incompleteText={t('comparison.radarIncomplete')} />}
      <div className="pc-metrics">{active.id === 'overview' ? metricCards : metricCards.slice(0, highlighted.length)}</div>
      </div>
      {active.id !== 'overview' && metricCards.length > highlighted.length
        && <div className="pc-metrics pc-extra-metrics">{metricCards.slice(highlighted.length)}</div>}
      <p className="pc-caption">{t('comparison.arrows')}</p>
      {!sameSource && selected.some(player => player.estadisticasAvanzadas) &&
        <p className="pc-caption">{t('comparison.sourceMismatch')}</p>}
      </>}
      </div>)}
    </section> : !both && <p className="pc-empty" role="status">{t('comparison.selectBoth')}</p>}
    {both && samePlayer(...selected) && <Alert severity="warning">{t('comparison.samePlayer')}</Alert>}
  </section>;
}
